import { useEffect, useState } from 'react';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Divider, HelpText, Switch } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { FormInput, Modal } from 'src/components/molecules';
import { createTagAction } from 'src/components/pages/Tags/actions/createTagAction';
import { deleteTagAction } from 'src/components/pages/Tags/actions/deleteTagAction';
import { updateTagAction } from 'src/components/pages/Tags/actions/updateTagAction';
import {
  TagType,
  useCreateTagMutation,
  useDeleteTagMutation,
  useUpdateTagMutation,
} from 'src/graphql';
import { AppDispatch } from 'src/store';
import { useTheme } from 'styled-components';
import {
  FormInputVariant,
  FormState,
  TagStatus,
  TagTypeStatus,
} from 'src/types';
import {
  DividerContainer,
  FormInputWrapper,
  HelpTextWrapper,
  StyledIconWrapper,
  TagTypeBodyContainer,
  TagTypeBodyStyled,
  TagTypeBodySubTitle,
  TagTypeBodyTitle,
  TagTypeBodyWrapper,
  TagTypeButtonList,
  TagTypeContainerLeft,
  TagTypeContainerRight,
  TagTypeIconButton,
  TagTypeSwitchTitle,
  TitleWrapper,
} from './TagTypeBody.styles';

interface Props {
  tagTypeId: string;
  updateTagTypeMutationisLoading: boolean;
  tagTypeName: string;
  tags?: TagType[];
  droppableId: string;
  recalculateCoordinates: () => void;
}

interface FormStateProps {
  id: string;
  value: string;
  status: TagStatus;
  tagId?: string;
  helpText?: string;
}

export const TagTypeBody = ({
  tagTypeId,
  tags,
  updateTagTypeMutationisLoading,
  tagTypeName,
  droppableId,
  recalculateCoordinates,
}: Props) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<{
    id: string;
    tagId: string | undefined;
  } | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState<FormStateProps[]>([]);

  const formInputsLength = formState.length;
  useEffect(() => {
    recalculateCoordinates();
  }, [formInputsLength, recalculateCoordinates]);

  const emptyStringHelpText = 'Tags cannot be blank. Please enter a valid tag.';
  const duplicateTagNameHelpText = `This tag already exists in ${
    tagTypeName ? tagTypeName : `this`
  } tag type. Please enter a unique tag.`;

  const { mutateAsync: createTagAsync, isLoading: isLoadingCreate } =
    useCreateTagMutation();
  const { mutateAsync: updateTagAsync, isLoading: isLoadingUpdate } =
    useUpdateTagMutation();
  const { mutateAsync: deleteTagAsync, isLoading: isLoadingDelete } =
    useDeleteTagMutation();

  const mutationIsLoading =
    updateTagTypeMutationisLoading ||
    isLoadingCreate ||
    isLoadingUpdate ||
    isLoadingDelete;

  const clearForm = (tags?: TagType[]) => {
    if (!tags || tags.length === 0) {
      setFormState([
        { id: crypto.randomUUID(), value: '', status: TagStatus.PAUSED },
      ]);
    } else {
      const initialInputs = tags.map((tag) => ({
        id: crypto.randomUUID(),
        value: tag.name,
        status: tag.status as TagStatus,
        tagId: tag.id,
      }));

      setFormState((currentFormState) => {
        const hasDraftInput = currentFormState.some(
          (input) => !input.tagId && !input.value,
        );
        if (hasDraftInput) {
          return [
            ...initialInputs,
            { id: crypto.randomUUID(), value: '', status: TagStatus.PAUSED },
          ];
        } else {
          return initialInputs;
        }
      });
    }
  };

  useEffect(() => {
    clearForm(tags);
  }, [tags]);

  const handleAddInput = () => {
    const newId = crypto.randomUUID();
    const newInputs = [
      ...formState,
      { id: newId, value: '', status: TagStatus.PAUSED },
    ];
    setFormState(newInputs);
  };

  const handleInputChange = (id: string, newValue: string) => {
    const updatedInputs = formState.map((input) =>
      input.id === id
        ? { ...input, value: newValue, helpText: undefined }
        : input,
    );
    setFormState(updatedInputs);
  };

  const handleRemoveInput = async (id: string, tagId?: string) => {
    if (formState.length === 1) {
      setFormState([
        { id: crypto.randomUUID(), value: '', status: TagStatus.PAUSED },
      ]);
    } else {
      setFormState(formState.filter((input) => input.id !== id));
    }

    await dispatch(
      deleteTagAction({
        mutationDeleteTag: deleteTagAsync,
        queryClient,
        tagId,
      }),
    );
  };

  const saveTag = async (
    value: string,
    index: number,
    status: TagStatus,
    tagId?: string,
  ) => {
    const updatedInputs = [...formState];
    updatedInputs[index].helpText = undefined;
    setFormState(updatedInputs);
    let duplicateTagName = false;
    let emptyTagName = false;

    if (tagId) {
      await dispatch(
        updateTagAction({
          tagTypeId,
          name: value,
          status,
          mutationUpdateTag: updateTagAsync,
          queryClient,
          tagId,
          setDuplicateTagName: (val) => {
            duplicateTagName = val;
          },
        }),
      );
    } else {
      await dispatch(
        createTagAction({
          tagTypeId,
          name: value,
          mutationAddTag: createTagAsync,
          queryClient,
          setDuplicateTagName: (val) => {
            duplicateTagName = val;
          },
          setEmptyTagName: (val) => {
            emptyTagName = val;
          },
        }),
      );
    }
    if (duplicateTagName) {
      updatedInputs[index].helpText = duplicateTagNameHelpText;
      setFormState([...updatedInputs]);
    }

    if (emptyTagName) {
      updatedInputs[index].helpText = emptyStringHelpText;
      setFormState([...updatedInputs]);
    }
    recalculateCoordinates();
  };

  const changeTagStatus = (
    currentTagStatus: string,
    name: string,
    tagId?: string,
  ) => {
    if (!tagId) return;

    dispatch(
      updateTagAction({
        tagId,
        tagTypeId,
        name,
        status:
          currentTagStatus === TagTypeStatus.LIVE
            ? TagTypeStatus.PAUSED
            : TagTypeStatus.LIVE,
        mutationUpdateTag: updateTagAsync,
        queryClient,
      }),
    );
  };

  const inputFieldIsEmpty = formState.some(
    (input) => input.value.trim() === '',
  );

  const disableRemoveButton = (tagId?: string) => {
    return formState.length === 1 && !tagId;
  };

  const theme = useTheme();
  const tagTypeNameLowercase = tagTypeName.toLowerCase();

  const handleTagClick = (input: FormStateProps) => {
    if (input.tagId) {
      setTagToDelete({
        id: input.id,
        tagId: input.tagId,
      });
      setShowConfirmModal(true);
    } else {
      handleRemoveInput(input.id);
    }
  };

  return (
    <TagTypeBodyStyled>
      <DividerContainer>
        <Divider
          variant="horizontal"
          color={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N300
              : theme.atlas.core.color.gray[200]
          }
        />
      </DividerContainer>
      <TagTypeBodyContainer>
        <TagTypeBodyWrapper>
          <TagTypeBodyTitle>
            Create {tagTypeNameLowercase} tags{' '}
            <TagTypeBodySubTitle>
              - Create and enable a tag to &apos;activate tag type&apos;
            </TagTypeBodySubTitle>
          </TagTypeBodyTitle>
          <TitleWrapper>
            <TagTypeSwitchTitle>Enable</TagTypeSwitchTitle>
          </TitleWrapper>
        </TagTypeBodyWrapper>
        <Droppable droppableId={droppableId} type="child">
          {(provided, droppableState) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {formState.map((input, index) => (
                <Draggable
                  key={index}
                  draggableId={input.id}
                  index={index}
                  isDragDisabled={!input.tagId}
                >
                  {(provided, draggableState) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <TagTypeBodyWrapper
                        key={input.id}
                        dimmed={
                          droppableState.isDraggingOver &&
                          !draggableState.isDragging
                        }
                      >
                        <TagTypeContainerLeft>
                          <FormInputWrapper>
                            <StyledIconWrapper>
                              <StyledIcon
                                size="20px"
                                className="uil-arrows-v"
                                color={
                                  input.tagId
                                    ? theme.isVaultVariant
                                      ? theme.newPalette.neutral.N800
                                      : theme.atlas.semantic.color.type.default
                                    : theme.isVaultVariant
                                      ? theme.newPalette.neutral.N400
                                      : theme.atlas.core.color.gray[400]
                                }
                                {...provided.dragHandleProps}
                              />
                            </StyledIconWrapper>
                            <FormInput
                              variant={FormInputVariant.INPUT}
                              placeholder="Example: High priority"
                              data-testid={`tag-input-${tagTypeId}`}
                              onChange={(e) =>
                                handleInputChange(input.id, e.target.value)
                              }
                              value={input.value}
                              state={
                                input.helpText
                                  ? FormState.ERROR
                                  : FormState.DEFAULT
                              }
                              onBlur={() =>
                                saveTag(
                                  input.value,
                                  index,
                                  input.status,
                                  input.tagId,
                                )
                              }
                              enterKeyAction={() =>
                                saveTag(
                                  input.value,
                                  index,
                                  input.status,
                                  input.tagId,
                                )
                              }
                            />
                          </FormInputWrapper>
                          {input.helpText && (
                            <HelpTextWrapper>
                              {
                                <HelpText
                                  state={FormState.ERROR}
                                  helpText={input.helpText}
                                  data-testid="tag-input-help-text"
                                  $margin="0"
                                />
                              }
                            </HelpTextWrapper>
                          )}
                        </TagTypeContainerLeft>
                        <TagTypeContainerRight>
                          <TagTypeButtonList>
                            <Switch
                              checked={input.status === TagStatus.LIVE}
                              data-testid={`tag-switch-button-${input.tagId}`}
                              onClick={() =>
                                changeTagStatus(
                                  input.status,
                                  input.value,
                                  input?.tagId,
                                )
                              }
                              disabled={
                                mutationIsLoading || input.value.trim() === ''
                              }
                            />
                            <TagTypeIconButton
                              size="medium"
                              variant="tertiary"
                              iconName="uil-trash-alt"
                              data-testid={`delete-tag-button-${input.tagId}`}
                              disabled={
                                disableRemoveButton(input.tagId) ||
                                mutationIsLoading
                              }
                              onClick={() => handleTagClick(input)}
                            />
                            <TagTypeIconButton
                              size="medium"
                              variant="tertiary"
                              iconName="uil-plus"
                              onClick={() => handleAddInput()}
                              data-testid={`add-tag-button-${input.tagId}`}
                              disabled={
                                mutationIsLoading ||
                                !!input.helpText ||
                                inputFieldIsEmpty
                              }
                            />
                          </TagTypeButtonList>
                        </TagTypeContainerRight>
                      </TagTypeBodyWrapper>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <Modal
          size="medium"
          title="Are you sure you want to delete this tag?"
          data-testid={`delete-tag-modal-${tagTypeId}`}
          description={
            <>
              <span>
                This action will delete the tag and cannot be undone. Deleting a
                tag will remove it from all cases where it has been applied.
              </span>
              <br></br>
              <br></br>
              <span>To confirm, press &quot;Delete&quot;.</span>
            </>
          }
          open={showConfirmModal}
          setOpen={setShowConfirmModal}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={() => {
            if (tagToDelete) {
              setShowConfirmModal(false);
              handleRemoveInput(tagToDelete.id, tagToDelete?.tagId);
              setTagToDelete(null);
            }
          }}
          confirmButtonText="Delete Tag"
        />
      </TagTypeBodyContainer>
      <DividerContainer>
        <Divider
          variant="horizontal"
          color={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N300
              : theme.atlas.core.color.gray[200]
          }
        />
      </DividerContainer>
    </TagTypeBodyStyled>
  );
};
